import type { LocalDate } from '../dates';
import { normalizeNote, noteTokens } from './notes';

export interface NoteHistoryItem {
  note: string | null;
  categoryId: string | null;
  date: LocalDate;
}

export interface CategorySuggestion {
  categoryId: string;
  basis: 'same_note' | 'similar_note';
}

export interface SuggestOptions {
  allowedCategoryIds?: ReadonlySet<string>;
  minSimilarity?: number; // default 0.5
}

/** Similarity of two already-normalized notes, 0..1. */
export function noteSimilarity(a: string, b: string): number {
  if (a === '' || b === '') return 0;
  if (a === b) return 1;
  const [short, long] = a.length <= b.length ? [a, b] : [b, a];
  if (short.length >= 3 && long.startsWith(short)) return 0.8;
  const ta = new Set(noteTokens(a));
  const tb = new Set(noteTokens(b));
  if (ta.size === 0 || tb.size === 0) return 0;
  let shared = 0;
  for (const t of ta) if (tb.has(t)) shared += 1;
  const union = ta.size + tb.size - shared;
  return Math.round((shared / union) * 100) / 100;
}

/**
 * Suggest a category for a manual entry from past entries with the same or a similar note.
 * Only the best-matching tier counts; within it the most used category wins, then the most recent.
 */
export function suggestCategory(
  note: string | null | undefined,
  history: readonly NoteHistoryItem[],
  opts: SuggestOptions = {},
): CategorySuggestion | null {
  const target = normalizeNote(note);
  if (target === '') return null;
  const min = opts.minSimilarity ?? 0.5;
  let best = 0;
  const tally = new Map<string, { count: number; last: LocalDate }>();

  for (const item of history) {
    if (!item.categoryId) continue;
    if (opts.allowedCategoryIds && !opts.allowedCategoryIds.has(item.categoryId)) continue;
    const score = noteSimilarity(target, normalizeNote(item.note));
    if (score < min) continue;
    if (score > best) {
      best = score;
      tally.clear();
    }
    if (score === best) {
      const t = tally.get(item.categoryId) ?? { count: 0, last: '' };
      t.count += 1;
      if (item.date > t.last) t.last = item.date;
      tally.set(item.categoryId, t);
    }
  }

  let pick: { id: string; count: number; last: LocalDate } | null = null;
  for (const [id, t] of tally) {
    if (
      !pick ||
      t.count > pick.count ||
      (t.count === pick.count && t.last > pick.last) ||
      (t.count === pick.count && t.last === pick.last && id < pick.id)
    ) {
      pick = { id, ...t };
    }
  }
  if (!pick) return null;
  return { categoryId: pick.id, basis: best === 1 ? 'same_note' : 'similar_note' };
}
